/**
 * Template-driven room decoration.
 * Places furniture, props, and features inside rectangular rooms
 * using configurable templates.
 */
export class RoomDecorator {
  constructor(rng) {
    this.rng = rng;
  }

  /**
   * Decorate a room using a template.
   * @param {Array<Array<number>>} grid - Map grid
   * @param {object} room - { x, y, width, height }
   * @param {object} template - Decoration template
   * @param {string} layer - Which grid layer to modify ('ground', 'objects', 'overhead')
   * @returns {object} Decoration results
   */
  decorate(grid, room, template, layer = 'objects') {
    const results = { placed: [] };

    if (!template || !template.features) return results;

    for (const feature of template.features) {
      switch (feature.type) {
        case 'border':
          this._placeBorder(grid, room, feature, results);
          break;
        case 'corners':
          this._placeCorners(grid, room, feature, results);
          break;
        case 'center':
          this._placeCenter(grid, room, feature, results);
          break;
        case 'scatter':
          this._placeScatter(grid, room, feature, results);
          break;
        case 'row':
          this._placeRow(grid, room, feature, results);
          break;
        case 'wall':
          this._placeWall(grid, room, feature, results);
          break;
      }
    }

    return results;
  }

  _placeBorder(grid, room, feature, results) {
    const { x, y, width: w, height: h } = room;
    const tile = feature.tileId;
    const inset = feature.inset || 0;

    for (let bx = x + inset; bx < x + w - inset; bx++) {
      this._setTile(grid, bx, y + inset, tile, results);
      this._setTile(grid, bx, y + h - 1 - inset, tile, results);
    }
    for (let by = y + inset; by < y + h - inset; by++) {
      this._setTile(grid, x + inset, by, tile, results);
      this._setTile(grid, x + w - 1 - inset, by, tile, results);
    }
  }

  _placeCorners(grid, room, feature, results) {
    const { x, y, width: w, height: h } = room;
    const tiles = feature.tileIds || [feature.tileId];
    const inset = feature.inset || 1;

    const corners = [
      { x: x + inset, y: y + inset },
      { x: x + w - 1 - inset, y: y + inset },
      { x: x + inset, y: y + h - 1 - inset },
      { x: x + w - 1 - inset, y: y + h - 1 - inset },
    ];

    for (const corner of corners) {
      const tile = this.rng.pick(tiles);
      this._setTile(grid, corner.x, corner.y, tile, results);
    }
  }

  _placeCenter(grid, room, feature, results) {
    const cx = Math.floor(room.x + room.width / 2);
    const cy = Math.floor(room.y + room.height / 2);

    if (feature.tiles) {
      // Multi-tile center piece
      for (const { dx, dy, tileId } of feature.tiles) {
        this._setTile(grid, cx + dx, cy + dy, tileId, results);
      }
    } else {
      this._setTile(grid, cx, cy, feature.tileId, results);
    }
  }

  _placeScatter(grid, room, feature, results) {
    const { x, y, width: w, height: h } = room;
    const count = feature.count || 3;
    const tiles = feature.tileIds || [feature.tileId];
    const margin = feature.margin || 1;
    const placed = new Set();

    for (let i = 0; i < count * 3 && placed.size < count; i++) {
      const px = this.rng.nextInt(x + margin, x + w - 1 - margin);
      const py = this.rng.nextInt(y + margin, y + h - 1 - margin);
      const key = `${px},${py}`;
      if (placed.has(key)) continue;
      if (grid[py]?.[px] !== 0 && feature.requireEmpty) continue;

      placed.add(key);
      const tile = this.rng.pick(tiles);
      this._setTile(grid, px, py, tile, results);
    }
  }

  _placeRow(grid, room, feature, results) {
    const { x, y, width: w, height: h } = room;
    const tile = feature.tileId;
    const dir = feature.direction || 'horizontal';
    const offset = feature.offset || Math.floor((dir === 'horizontal' ? h : w) / 2);
    const spacing = feature.spacing || 2;
    const margin = feature.margin || 1;

    if (dir === 'horizontal') {
      const ry = y + offset;
      for (let rx = x + margin; rx < x + w - margin; rx += spacing) {
        this._setTile(grid, rx, ry, tile, results);
      }
    } else {
      const rx = x + offset;
      for (let ry = y + margin; ry < y + h - margin; ry += spacing) {
        this._setTile(grid, rx, ry, tile, results);
      }
    }
  }

  _placeWall(grid, room, feature, results) {
    const { x, y, width: w, height: h } = room;
    const tile = feature.tileId;
    const side = feature.side || 'north';
    const spacing = feature.spacing || 1;
    const margin = feature.margin || 1;

    switch (side) {
      case 'north':
        for (let bx = x + margin; bx < x + w - margin; bx += spacing)
          this._setTile(grid, bx, y + 1, tile, results);
        break;
      case 'south':
        for (let bx = x + margin; bx < x + w - margin; bx += spacing)
          this._setTile(grid, bx, y + h - 2, tile, results);
        break;
      case 'west':
        for (let by = y + margin; by < y + h - margin; by += spacing)
          this._setTile(grid, x + 1, by, tile, results);
        break;
      case 'east':
        for (let by = y + margin; by < y + h - margin; by += spacing)
          this._setTile(grid, x + w - 2, by, tile, results);
        break;
    }
  }

  _setTile(grid, x, y, tileId, results) {
    if (y >= 0 && y < grid.length && x >= 0 && x < grid[0].length) {
      grid[y][x] = tileId;
      results.placed.push({ x, y, tileId });
    }
  }
}
